import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type StackedBarTone = 'ok' | 'warn' | 'error' | 'off' | 'unknown';

export interface StackedBarSegment {
  label: string;
  count: number;
  tone: StackedBarTone;
}

/**
 * Count-based stacked bar with the look of `app-power-threshold-bar`: a 10px
 * hairline capsule with no gaps between segments (each `count / total` wide),
 * an optional scale row (0, the running total at each segment boundary, total
 * — placed at their true %) and an optional legend of dot + label + count.
 * Used for feed/stop health on the provider screens.
 *
 *   <app-stacked-bar [segments]="[{ label: 'OK', count: 12, tone: 'ok' }, …]" />
 *
 * Fills use colorscale primitives (there are no semantic tokens for band
 * fills), as power-threshold-bar does; `unknown` is the same diagonal stripe
 * idea as its hysteresis bands. With no counts the track renders empty.
 */
@Component({
  selector: 'app-stacked-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div>
      <div
        class="flex overflow-hidden rounded-full shadow-hairline"
        [class.bg-surface-2]="total() === 0"
        [style.height.px]="height()"
      >
        @for (seg of visible(); track $index) {
          <div [class]="'tone-' + seg.tone" [style.width.%]="seg.pct"></div>
        }
      </div>
      @if (labels() && total() > 0) {
        <!-- Labels sit at their true % along the bar, not evenly spaced. -->
        <div class="relative mt-1.5 h-3.5 font-num text-[10px] font-semibold text-ink-3">
          <span class="absolute left-0">0</span>
          @for (b of boundaries(); track $index) {
            <span class="absolute -translate-x-1/2" [style.left.%]="b.pct">{{ b.value }}</span>
          }
          <span class="absolute right-0">{{ total() }}</span>
        </div>
      }
      @if (legend()) {
        <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          @for (seg of segments(); track $index) {
            <span class="flex items-center gap-1.5 text-[12px] text-ink-2">
              <span class="h-2 w-2 flex-none rounded-full" [class]="'tone-' + seg.tone"></span>
              {{ seg.label }}
              <span class="font-bold text-ink-1">{{ seg.count }}</span>
            </span>
          }
        </div>
      }
    </div>
  `,
  styles: `
    .tone-ok {
      background: var(--colorscales-green-500);
    }
    .tone-warn {
      background: var(--colorscales-orange-300);
    }
    .tone-error {
      background: var(--colorscales-red-300);
    }
    .tone-off {
      background: var(--color-surface-3);
    }
    .tone-unknown {
      background: repeating-linear-gradient(
        135deg,
        var(--color-surface-2) 0 5px,
        var(--color-surface-3) 5px 10px
      );
    }
  `,
})
export class StackedBar {
  readonly segments = input.required<StackedBarSegment[]>();
  readonly height = input(10);
  /** Render the 0 … boundaries … total scale row under the bar. */
  readonly labels = input(true);
  /** Render the dot + label + count legend row. */
  readonly legend = input(true);

  protected readonly total = computed(() => this.segments().reduce((n, s) => n + Math.max(0, s.count), 0));

  /** Non-empty segments with their width in percent points. */
  protected readonly visible = computed(() => {
    const total = this.total();
    if (!total) return [];
    return this.segments()
      .filter((s) => s.count > 0)
      .map((s) => ({ tone: s.tone, pct: (s.count / total) * 100 }));
  });

  /** Running total at each inner boundary between non-empty segments
   *  (never at 0 or at the total, which have their own labels). */
  protected readonly boundaries = computed(() => {
    const total = this.total();
    const out: { value: number; pct: number }[] = [];
    let running = 0;
    for (const s of this.segments()) {
      if (s.count <= 0) continue;
      running += s.count;
      if (running > 0 && running < total) out.push({ value: running, pct: (running / total) * 100 });
    }
    return out;
  });
}
